import { Container, Title, Text, Stack, List, Anchor } from '@mantine/core';
import { Layout } from '../shared/components/Layout';
import { useSEO } from '../shared/hooks';

export function PrivacyPolicy() {
  useSEO({
    title: 'Polityka Prywatności',
    description: 'Polityka prywatności platformy Taryfy Pradu. Informacje o przetwarzaniu danych osobowych, RODO, pliki cookie.',
    canonicalPath: '/privacy-policy',
  });
  return (
    <Layout isAuthenticated={false}>
      <Container size="md" py={80}>
        <Stack gap="xl">
          {/* Header */}
          <div>
            <Title order={1} mb="md">
              Polityka Prywatności
            </Title>
            <Text c="dimmed" mb="xl">
              Ostatnia aktualizacja: 1 lutego 2026
            </Text>
            <Text size="lg" c="dimmed">
              Niniejsza Polityka Prywatności wyjaśnia, jakie dane osobowe zbieramy, w jaki sposób
              je wykorzystujemy i chronimy, oraz jakie prawa przysługują Ci w związku z
              przetwarzaniem Twoich danych osobowych.
            </Text>
          </div>

          {/* Administrator */}
          <div>
            <Title order={2} size="h3" mb="md">
              1. Administrator danych osobowych
            </Title>
            <Text c="dimmed" mb="sm">
              Administratorem danych osobowych jest:
            </Text>
            <Stack gap="xs" mb="md">
              <Text c="dimmed">
                <strong>Robert Boguszewski</strong>
              </Text>
              <Text c="dimmed">
                ul. Królowej Jadwigi 34a, 32-040 Świątniki Górne
              </Text>
              <Text c="dimmed">
                NIP: 944-203-67-90, REGON: 362403341
              </Text>
            </Stack>
            <Text c="dimmed" mb="xs">
              <strong>Kontakt w sprawach dotyczących ochrony danych osobowych (RODO):</strong>{' '}
              <Anchor href="mailto:rodo@taryfypradu.pl">rodo@taryfypradu.pl</Anchor>
            </Text>
            <Text c="dimmed">
              <strong>Kontakt ogólny:</strong>{' '}
              <Anchor href="mailto:kontakt@taryfypradu.pl">kontakt@taryfypradu.pl</Anchor>
            </Text>
          </div>

          {/* Data Collection */}
          <div>
            <Title order={2} size="h3" mb="md">
              2. Jakie dane zbieramy?
            </Title>
            <Text c="dimmed" mb="sm">
              W zależności od sposobu korzystania z naszej platformy możemy zbierać następujące
              dane osobowe:
            </Text>
            <List spacing="sm" c="dimmed">
              <List.Item>
                <strong>Dane rejestracyjne:</strong> adres email, hasło (w formie zaszyfrowanej)
              </List.Item>
              <List.Item>
                <strong>Dane zużycia energii:</strong> wprowadzone przez Ciebie dane dotyczące
                zużycia energii elektrycznej, strefy cenowej, typu taryfy
              </List.Item>
              <List.Item>
                <strong>Dane obliczeń:</strong> zapisane wyniki porównań taryf, historia obliczeń
              </List.Item>
              <List.Item>
                <strong>Dane techniczne:</strong> adres IP, typ przeglądarki, system operacyjny,
                dane dotyczące korzystania z platformy (logi)
              </List.Item>
            </List>
          </div>

          {/* CSV Export - Data Portability */}
          <div>
            <Title order={2} size="h3" mb="md">
              2A. Eksport danych (prawo do przenoszenia danych)
            </Title>
            <Text c="dimmed" mb="sm">
              Oferujemy funkcję eksportu danych do pliku CSV, która jest realizacją Twojego prawa
              do przenoszenia danych zgodnie z art. 20 RODO.
            </Text>
            <Stack gap="sm">
              <Text c="dimmed">
                <strong>Jakie dane są eksportowane:</strong>
              </Text>
              <List spacing="xs" c="dimmed" ml="md">
                <List.Item>
                  Godzinowe dane zużycia energii elektrycznej (8760 wierszy rocznie)
                </List.Item>
                <List.Item>
                  Szczegółowe koszty dla wszystkich porównywanych taryf
                </List.Item>
                <List.Item>
                  Informacje o strefach czasowych (dzień/noc, szczyt/poza szczytem)
                </List.Item>
                <List.Item>
                  Dane o dniach roboczych, weekendowych i świętach
                </List.Item>
              </List>
              <Text c="dimmed" mt="xs">
                <strong>Format eksportu:</strong> CSV (Comma-Separated Values) - uniwersalny format
                możliwy do otwarcia w programach Excel, Google Sheets i innych arkuszach kalkulacyjnych.
              </Text>
              <Text c="dimmed">
                <strong>Przetwarzanie:</strong> Eksport odbywa się lokalnie w przeglądarce - dane nie są
                przesyłane na serwer podczas generowania pliku CSV. Plik jest zapisywany bezpośrednio
                na Twoim urządzeniu.
              </Text>
              <Text c="dimmed" fw={600} style={{ color: 'var(--mantine-color-orange-7)' }}>
                ⚠️ Po pobraniu pliku CSV jesteś odpowiedzialny/a za jego bezpieczne przechowywanie.
                Plik zawiera Twoje dane osobowe i szczegółowe informacje o zużyciu energii.
              </Text>
            </Stack>
          </div>

          {/* Purpose of Processing */}
          <div>
            <Title order={2} size="h3" mb="md">
              3. Cel i podstawa prawna przetwarzania danych
            </Title>
            <Stack gap="md">
              <div>
                <Text fw={600} mb="xs">
                  3.1. Świadczenie usług platformy
                </Text>
                <Text c="dimmed">
                  Przetwarzamy dane w celu umożliwienia korzystania z kalkulatora taryf, zapisywania
                  obliczeń i zarządzania kontem użytkownika. Podstawa prawna: wykonanie umowy
                  (art. 6 ust. 1 lit. b RODO).
                </Text>
              </div>
              <div>
                <Text fw={600} mb="xs">
                  3.2. Bezpieczeństwo i zapobieganie nadużyciom
                </Text>
                <Text c="dimmed">
                  Przetwarzamy dane w celu zapewnienia bezpieczeństwa platformy, wykrywania
                  i zapobiegania nadużyciom. Podstawa prawna: prawnie uzasadniony interes
                  administratora (art. 6 ust. 1 lit. f RODO).
                </Text>
              </div>
              <div>
                <Text fw={600} mb="xs">
                  3.3. Ulepszanie platformy
                </Text>
                <Text c="dimmed">
                  Analizujemy sposób korzystania z platformy w celu jej ulepszania i optymalizacji.
                  Dane są przetwarzane w formie zagregowanej i zanonimizowanej. Podstawa prawna:
                  prawnie uzasadniony interes administratora (art. 6 ust. 1 lit. f RODO).
                </Text>
              </div>
            </Stack>
          </div>

          {/* Data Security */}
          <div>
            <Title order={2} size="h3" mb="md">
              4. Jak chronimy Twoje dane?
            </Title>
            <Text c="dimmed" mb="sm">
              Stosujemy odpowiednie środki techniczne i organizacyjne zapewniające bezpieczeństwo
              Twoich danych osobowych:
            </Text>
            <List spacing="sm" c="dimmed">
              <List.Item>
                Szyfrowanie połączeń (HTTPS/TLS) dla wszystkich transmisji danych
              </List.Item>
              <List.Item>
                Przechowywanie danych na bezpiecznych serwerach AWS w regionie europejskim
                (Frankfurt, Niemcy)
              </List.Item>
              <List.Item>
                Szyfrowanie haseł użytkowników (hasła nigdy nie są przechowywane w formie jawnej)
              </List.Item>
              <List.Item>
                Ograniczony dostęp do danych osobowych tylko dla uprawnionych osób
              </List.Item>
              <List.Item>
                Regularne kopie zapasowe danych
              </List.Item>
              <List.Item>
                Monitorowanie bezpieczeństwa i logowanie dostępu do danych
              </List.Item>
            </List>
            <Text c="dimmed" mt="sm" fw={600}>
              Nigdy nie przechowujemy haseł do systemów dostawców energii (eLicznik) - wszelkie
              połączenia są przetwarzane w pamięci i natychmiast usuwane.
            </Text>
          </div>

          {/* User Responsibility for Downloaded Files */}
          <div>
            <Title order={2} size="h3" mb="md">
              4A. Odpowiedzialność użytkownika za pobrane pliki
            </Title>
            <Text c="dimmed" mb="sm">
              Pobierając plik CSV z Twoimi danymi, przejmujesz pełną odpowiedzialność za jego
              bezpieczeństwo i przechowywanie. Administrator nie ponosi odpowiedzialności za
              ewentualne wycieki danych z plików pobranych na Twoje urządzenie.
            </Text>
            <Stack gap="sm">
              <Text c="dimmed">
                <strong>Zalecane praktyki bezpieczeństwa:</strong>
              </Text>
              <List spacing="xs" c="dimmed" ml="md">
                <List.Item>
                  <strong>Przechowuj pliki CSV w bezpiecznym miejscu</strong> - używaj zaszyfrowanych
                  folderów lub dysków
                </List.Item>
                <List.Item>
                  <strong>Nie udostępniaj plików osobom trzecim</strong> - zawierają one Twoje dane
                  osobowe i szczegółowe informacje o zużyciu energii
                </List.Item>
                <List.Item>
                  <strong>Usuń pliki po wykorzystaniu</strong> - jeśli plik CSV nie jest już potrzebny,
                  usuń go w sposób trwały (np. z użyciem shred, secure delete)
                </List.Item>
                <List.Item>
                  <strong>Unikaj wysyłania emailem</strong> - nie wysyłaj plików CSV przez niezabezpieczoną
                  pocztę elektroniczną
                </List.Item>
                <List.Item>
                  <strong>Skanuj urządzenia antywirusem</strong> - upewnij się, że Twoje urządzenie
                  jest wolne od złośliwego oprogramowania
                </List.Item>
                <List.Item>
                  <strong>Używaj hasła do kompresji</strong> - jeśli archiwizujesz pliki, użyj
                  archiwum chronionego hasłem (np. ZIP, 7z z szyfrowaniem)
                </List.Item>
              </List>
              <Text c="dimmed" mt="xs">
                <strong>Przypomnienie:</strong> Po pobraniu pliku CSV z danymi, staje się on
                Twoją własnością i odpowiadasz za jego zabezpieczenie zgodnie z własnymi potrzebami
                i wymogami bezpieczeństwa.
              </Text>
              <Text c="dimmed" fw={600} style={{ color: 'var(--mantine-color-red-7)' }}>
                ⚠️ Administrator nie ma kontroli nad plikami pobranymi na Twoje urządzenie i nie może
                zapewnić ich bezpieczeństwa po opuszczeniu platformy. Odpowiedzialność za bezpieczeństwo
                pobranych plików spoczywa wyłącznie na Tobie.
              </Text>
            </Stack>
          </div>

          {/* Data Breach Notification */}
          <div>
            <Title order={2} size="h3" mb="md">
              4B. Powiadomienie o naruszeniu ochrony danych
            </Title>
            <Stack gap="sm">
              <Text c="dimmed">
                W przypadku naruszenia ochrony danych osobowych, które może powodować wysokie
                ryzyko naruszenia Twoich praw lub wolności, Administrator poinformuje Cię
                niezwłocznie (w ciągu 72 godzin od wykrycia naruszenia) zgodnie z wymogami
                Rozporządzenia o Ochronie Danych Osobowych (RODO).
              </Text>
              <Text c="dimmed">
                Powiadomienie będzie zawierało:
              </Text>
              <List spacing="xs" c="dimmed" ml="md">
                <List.Item>
                  Opis charakteru naruszenia ochrony danych osobowych
                </List.Item>
                <List.Item>
                  Imię i nazwisko oraz dane kontaktowe Inspektora Ochrony Danych (jeśli dotyczy)
                </List.Item>
                <List.Item>
                  Opis prawdopodobnych konsekwencji naruszenia
                </List.Item>
                <List.Item>
                  Opis środków podjętych lub proponowanych w celu zaradzenia naruszeniu
                </List.Item>
                <List.Item>
                  Zalecenia dotyczące działań, które możesz podjąć w celu zminimalizowania ryzyka
                </List.Item>
              </List>
              <Text c="dimmed">
                Powiadomienie zostanie wysłane na adres email przypisany do Twojego konta.
                W przypadku poważnego naruszenia możemy również użyć dodatkowych kanałów komunikacji
                (np. powiadomienia w serwisie, komunikat na stronie głównej).
              </Text>
            </Stack>
          </div>

          {/* Data Sharing */}
          <div>
            <Title order={2} size="h3" mb="md">
              5. Czy udostępniamy Twoje dane?
            </Title>
            <Text c="dimmed" mb="sm">
              Twoje dane osobowe nie są sprzedawane ani udostępniane podmiotom trzecim w celach
              marketingowych. Możemy udostępnić dane następującym kategoriom odbiorców:
            </Text>
            <List spacing="sm" c="dimmed">
              <List.Item>
                <strong>Dostawcy usług IT:</strong> Amazon Web Services (AWS) - hosting i
                infrastruktura chmurowa w regionie EU
              </List.Item>
              <List.Item>
                <strong>Organy publiczne:</strong> jeśli jest to wymagane przepisami prawa
                lub w odpowiedzi na uzasadnione żądania organów państwowych
              </List.Item>
            </List>
          </div>

          {/* Data Retention */}
          <div>
            <Title order={2} size="h3" mb="md">
              6. Jak długo przechowujemy dane?
            </Title>
            <List spacing="sm" c="dimmed">
              <List.Item>
                <strong>Dane konta użytkownika:</strong> do momentu usunięcia konta lub żądania
                usunięcia danych
              </List.Item>
              <List.Item>
                <strong>Dane obliczeń:</strong> do momentu usunięcia przez użytkownika lub
                usunięcia konta
              </List.Item>
              <List.Item>
                <strong>Logi systemowe:</strong> maksymalnie 12 miesięcy
              </List.Item>
            </List>
          </div>

          {/* User Rights */}
          <div>
            <Title order={2} size="h3" mb="md">
              7. Twoje prawa
            </Title>
            <Text c="dimmed" mb="sm">
              Zgodnie z RODO przysługują Ci następujące prawa:
            </Text>
            <List spacing="sm" c="dimmed">
              <List.Item>
                <strong>Prawo dostępu:</strong> możesz uzyskać informację, jakie dane osobowe
                są przetwarzane
              </List.Item>
              <List.Item>
                <strong>Prawo do sprostowania:</strong> możesz poprosić o poprawienie
                nieprawidłowych danych
              </List.Item>
              <List.Item>
                <strong>Prawo do usunięcia (prawo do bycia zapomnianym):</strong> możesz
                zażądać usunięcia swoich danych osobowych
              </List.Item>
              <List.Item>
                <strong>Prawo do ograniczenia przetwarzania:</strong> możesz poprosić
                o ograniczenie przetwarzania danych w określonych przypadkach
              </List.Item>
              <List.Item>
                <strong>Prawo do przenoszenia danych:</strong> możesz otrzymać swoje dane
                w ustrukturyzowanym formacie. Prawo to jest realizowane poprzez funkcję eksportu
                do pliku CSV dostępną w kalkulatorze (patrz Sekcja 2A). Eksport obejmuje wszystkie
                Twoje dane o zużyciu energii i wyniki obliczeń w formacie możliwym do wykorzystania
                w innych systemach.
              </List.Item>
              <List.Item>
                <strong>Prawo do sprzeciwu:</strong> możesz wnieść sprzeciw wobec przetwarzania
                danych
              </List.Item>
              <List.Item>
                <strong>Prawo do wycofania zgody:</strong> w każdej chwili możesz wycofać zgodę
                na przetwarzanie danych
              </List.Item>
              <List.Item>
                <strong>Prawo do wniesienia skargi:</strong> możesz złożyć skargę do Prezesa
                Urzędu Ochrony Danych Osobowych
              </List.Item>
            </List>
            <Text c="dimmed" mt="sm">
              Aby skorzystać ze swoich praw, skontaktuj się z nami:
            </Text>
            <Stack gap="xs" mt="xs">
              <Text c="dimmed">
                <strong>Sprawy RODO:</strong>{' '}
                <Anchor href="mailto:rodo@taryfypradu.pl">rodo@taryfypradu.pl</Anchor>
              </Text>
              <Text c="dimmed">
                <strong>Sprawy ogólne:</strong>{' '}
                <Anchor href="mailto:kontakt@taryfypradu.pl">kontakt@taryfypradu.pl</Anchor>
              </Text>
            </Stack>
          </div>

          {/* Cookies */}
          <div>
            <Title order={2} size="h3" mb="md">
              8. Pliki cookie i zgoda na przetwarzanie
            </Title>
            <Text c="dimmed" mb="sm">
              Nasza platforma wykorzystuje pliki cookie zgodnie z Dyrektywą ePrivacy oraz RODO.
              Rozróżniamy dwie kategorie plików cookie:
            </Text>

            <Stack gap="md">
              <div>
                <Text fw={600} mb="xs">
                  8.1. Pliki cookie niezbędne (Essential Cookies)
                </Text>
                <Text c="dimmed" mb="xs">
                  Podstawa prawna: <strong>Prawnie uzasadniony interes administratora</strong> (art. 6 ust. 1 lit. f RODO)
                  i niezbędność dla świadczenia usługi (art. 6 ust. 1 lit. b RODO).
                </Text>
                <Text c="dimmed" mb="sm">
                  Pliki cookie niezbędne do funkcjonowania platformy. <strong>Nie wymagają zgody użytkownika</strong>,
                  ponieważ są konieczne do świadczenia usługi:
                </Text>
                <List spacing="xs" c="dimmed" ml="md">
                  <List.Item>
                    Utrzymanie sesji zalogowanego użytkownika (authentication token)
                  </List.Item>
                  <List.Item>
                    Zapewnienie bezpieczeństwa platformy (CSRF protection)
                  </List.Item>
                  <List.Item>
                    Zapamiętywanie ustawień użytkownika (preferencje interfejsu)
                  </List.Item>
                  <List.Item>
                    Zapamiętywanie zgody na pliki cookie (cookie_consent)
                  </List.Item>
                </List>
              </div>

              <div>
                <Text fw={600} mb="xs">
                  8.2. Pliki cookie analityczne (Analytics Cookies)
                </Text>
                <Text c="dimmed" mb="xs">
                  Podstawa prawna: <strong>Zgoda użytkownika</strong> (art. 6 ust. 1 lit. a RODO).
                </Text>
                <Text c="dimmed" mb="sm">
                  Pliki cookie analityczne (Google Analytics 4) służą do analizy sposobu korzystania
                  z platformy i jej ulepszania. <strong>Wymagają wyraźnej zgody użytkownika</strong>:
                </Text>
                <List spacing="xs" c="dimmed" ml="md">
                  <List.Item>
                    Google Analytics 4 - pomiar ruchu i analiza zachowań użytkowników
                  </List.Item>
                  <List.Item>
                    Dane zbierane w formie zanonimizowanej (brak identyfikacji konkretnych użytkowników)
                  </List.Item>
                  <List.Item>
                    Służą wyłącznie do celów statystycznych i ulepszania platformy
                  </List.Item>
                </List>
              </div>

              <div>
                <Text fw={600} mb="xs">
                  8.3. Baner zgody na pliki cookie
                </Text>
                <Text c="dimmed">
                  Przy pierwszej wizycie na stronie zobaczysz baner z informacją o plikach cookie.
                  Masz możliwość wyboru:
                </Text>
                <List spacing="xs" c="dimmed" ml="md">
                  <List.Item>
                    <strong>"Akceptuj wszystkie"</strong> - wyrażasz zgodę na wszystkie pliki cookie,
                    w tym analityczne
                  </List.Item>
                  <List.Item>
                    <strong>"Tylko niezbędne"</strong> - akceptujesz wyłącznie pliki cookie niezbędne
                    do funkcjonowania platformy (bez Google Analytics)
                  </List.Item>
                </List>
                <Text c="dimmed" mt="xs">
                  Zgoda na pliki cookie analityczne jest <strong>dobrowolna</strong> i może być
                  <strong> wycofana w dowolnym momencie</strong> poprzez usunięcie pliku cookie
                  "cookie_consent" w ustawieniach przeglądarki. Po wycofaniu zgody baner pojawi się
                  ponownie przy następnej wizycie.
                </Text>
              </div>

              <div>
                <Text fw={600} mb="xs">
                  8.4. Zarządzanie plikami cookie
                </Text>
                <Text c="dimmed">
                  Możesz zarządzać plikami cookie w ustawieniach swojej przeglądarki:
                </Text>
                <List spacing="xs" c="dimmed" ml="md">
                  <List.Item>
                    Chrome: Ustawienia → Prywatność i bezpieczeństwo → Pliki cookie
                  </List.Item>
                  <List.Item>
                    Firefox: Opcje → Prywatność i bezpieczeństwo → Pliki cookie
                  </List.Item>
                  <List.Item>
                    Safari: Preferencje → Prywatność → Zarządzaj danymi witryn
                  </List.Item>
                </List>
                <Text c="dimmed" mt="xs" fw={600} style={{ color: 'var(--mantine-color-orange-7)' }}>
                  ⚠️ Wyłączenie plików cookie niezbędnych może ograniczyć funkcjonalność platformy
                  (np. uniemożliwić logowanie lub zapisywanie ustawień).
                </Text>
              </div>
            </Stack>
          </div>

          {/* Children Privacy */}
          <div>
            <Title order={2} size="h3" mb="md">
              9. Prywatność dzieci
            </Title>
            <Text c="dimmed">
              Nasza platforma nie jest przeznaczona dla osób poniżej 16 roku życia. Nie zbieramy
              świadomie danych osobowych od dzieci. Jeśli jesteś rodzicem lub opiekunem i uważasz,
              że Twoje dziecko udostępniło nam dane osobowe, skontaktuj się z nami, abyśmy mogli
              usunąć te dane.
            </Text>
          </div>

          {/* Changes to Policy */}
          <div>
            <Title order={2} size="h3" mb="md">
              10. Zmiany w Polityce Prywatności
            </Title>
            <Text c="dimmed">
              Możemy okresowo aktualizować niniejszą Politykę Prywatności. O istotnych zmianach
              poinformujemy Cię poprzez wyraźne powiadomienie na platformie lub wiadomość email.
              Data ostatniej aktualizacji znajduje się na górze tego dokumentu.
            </Text>
          </div>

          {/* Contact */}
          <div>
            <Title order={2} size="h3" mb="md">
              11. Kontakt
            </Title>
            <Text c="dimmed" mb="sm">
              Jeśli masz pytania dotyczące niniejszej Polityki Prywatności lub chcesz skorzystać
              ze swoich praw, skontaktuj się z nami:
            </Text>
            <Stack gap="xs">
              <Text c="dimmed">
                <strong>Administrator:</strong> Robert Boguszewski
              </Text>
              <Text c="dimmed">
                <strong>Adres:</strong> ul. Królowej Jadwigi 34a, 32-040 Świątniki Górne
              </Text>
              <Text c="dimmed">
                <strong>NIP:</strong> 944-203-67-90, <strong>REGON:</strong> 362403341
              </Text>
              <Text c="dimmed" mt="sm">
                <strong>Email - RODO i prywatność:</strong>{' '}
                <Anchor href="mailto:rodo@taryfypradu.pl">rodo@taryfypradu.pl</Anchor>
              </Text>
              <Text c="dimmed">
                <strong>Email - sprawy ogólne:</strong>{' '}
                <Anchor href="mailto:kontakt@taryfypradu.pl">kontakt@taryfypradu.pl</Anchor>
              </Text>
            </Stack>
          </div>

          {/* Data Transfers */}
          <div>
            <Title order={2} size="h3" mb="md">
              12. Przekazywanie danych poza Europejski Obszar Gospodarczy (EOG)
            </Title>
            <Stack gap="sm">
              <Title order={3} size="h4" fw={600}>
                12.1. Przechowywanie danych w UE
              </Title>
              <Text c="dimmed">
                Twoje dane osobowe są przechowywane i przetwarzane wyłącznie na serwerach
                Amazon Web Services (AWS) znajdujących się w regionie europejskim
                (Frankfurt, Niemcy - region eu-central-1), w ramach Europejskiego Obszaru
                Gospodarczego.
              </Text>

              <Title order={3} size="h4" fw={600} mt="md">
                12.2. Google Analytics 4
              </Title>
              <Text c="dimmed">
                Korzystamy z narzędzia Google Analytics 4 do analizy ruchu na stronie.
                Google LLC ma siedzibę w Stanach Zjednoczonych. Dane są przekazywane
                do Google na podstawie:
              </Text>
              <List spacing="xs" c="dimmed" ml="xl">
                <List.Item>
                  Standardowych Klauzul Umownych (SCC) zatwierdzonych przez Komisję Europejską
                </List.Item>
                <List.Item>
                  Dodatkowych środków bezpieczeństwa (pseudonimizacja, anonimizacja IP)
                </List.Item>
                <List.Item>
                  Możesz wycofać zgodę na cookies analityczne w każdej chwili
                </List.Item>
              </List>
              <Text c="dimmed" mt="sm">
                Więcej informacji:{' '}
                <Anchor
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://policies.google.com/privacy
                </Anchor>
              </Text>
            </Stack>
          </div>

          {/* GDPR Notice */}
          <div style={{
            backgroundColor: 'var(--mantine-color-blue-0)',
            padding: '1.5rem',
            borderRadius: '8px',
            borderLeft: '4px solid var(--mantine-color-blue-6)'
          }}>
            <Text fw={600} mb="xs" c="blue">
              Zgodność z RODO
            </Text>
            <Text size="sm" c="dimmed">
              Niniejsza Polityka Prywatności została opracowana zgodnie z wymogami Rozporządzenia
              Parlamentu Europejskiego i Rady (UE) 2016/679 z dnia 27 kwietnia 2016 r. w sprawie
              ochrony osób fizycznych w związku z przetwarzaniem danych osobowych (RODO).
            </Text>
          </div>
        </Stack>
      </Container>
    </Layout>
  );
}
